import React, { ReactNode, useContext, useEffect, useMemo } from "react";
import { SIGNAL_EXTRA_DETAILS_PATH } from "common/constants";
import { useSelector } from "react-redux";
import { EditSignalContext } from "features/signals/EditSignal/EditSignal.context";
import { useRouteMatch } from "react-router-dom";
import SignalFollowActions from "features/signals/SignalFollow/SignalFollowActions";
import DrawerTitleLayout from "common/Drawers/DrawerTitleLayout";
import Button from "common/Buttons/Button";
import CancelSignalTrigger from "features/signals/CancelSignal/CancelSignalTrigger";
import { selectSignalGeneralInfo } from "@shared/slices/signalExtraDetails.slice";
import TextWithIcon from "common/Text/TextWithIcon";
import { ICON_TYPES } from "@shared/common/constants";
import { getFollowSignalData } from "@shared/features/signals/common/utils";
import DebouncedButton from "common/Buttons/DebouncedButton";
import useBackButtonHandler from "utils/hooks/useBackButtonHandler.ts";
import { SignalDetailsContext } from "@shared/features/signals/common/SignalDetails.context";
import SignalActions from "features/signals/SignalDrawer/Title/SignalActions.tsx";
import { useUser } from "@shared/utils/hooks/misc.ts";
import { OnEditModeClicked } from "features/signals/common/types.ts";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { useTheme } from "styled-components";
import { useBottomNavigator } from "utils/hooks/misc";
import NewLookDrawerTitle, {
  parseLeverage,
} from "features/trades/NewLook/NewLookDrawerTitle.tsx";

interface DrawerTitleProps {
  onEditModeClicked: OnEditModeClicked;
  onCloseClicked: () => boolean;
}

const DrawerTitle = ({
  onEditModeClicked,
  onCloseClicked,
}: DrawerTitleProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const { isNewLook } = useTheme();
  const { baseShouldDisplay: isPhone } = useBottomNavigator();
  const { user } = useUser();
  const { isEdit, exchangeSignalArgs, forAdmin, showEditSignalActions } =
    useContext(SignalDetailsContext)!;
  const generalDataSelector = useMemo(
    () => selectSignalGeneralInfo(exchangeSignalArgs),
    [exchangeSignalArgs]
  );
  const signalGeneralData = useSelector(generalDataSelector);
  const isOpen = signalGeneralData?.is_open;
  const {
    onSaveChangesClick,
    editSignalLoading,
    editSignalConfigurationLoading,
  } = useContext(EditSignalContext)!;
  const extraDetailsMatch = useRouteMatch<{ signalId: string }>(
    SIGNAL_EXTRA_DETAILS_PATH
  );
  const symbol = signalGeneralData?.symbol;
  const { registerBackButtonListener } = useBackButtonHandler();
  const signalFollowData = useMemo(
    () =>
      signalGeneralData ? getFollowSignalData(signalGeneralData) : undefined,
    [signalGeneralData]
  );
  const showEllipsisSignalActions = user?.isAssetManager() && !isEdit;

  useEffect(() => {
    if (isEdit) {
      return registerBackButtonListener(onCloseClicked);
    }
  }, [isEdit]);

  const signalId =
    extraDetailsMatch?.params?.signalId &&
    parseFloat(extraDetailsMatch?.params?.signalId);

  const getTitleText = () => {
    return t("drawer.title", {
      symbol,
      status: isOpen ? t("status.open") : t("status.closed"),
    });
  };

  const renderLeftActions = () => {
    if (showEllipsisSignalActions) return null;
    let adminButtons: ReactNode | undefined;
    if (showEditSignalActions) {
      if (isEdit) {
        adminButtons = (
          <Button onClick={onCloseClicked}>{t("drawer.cancel")}</Button>
        );
      } else if (!user?.isAssetManager()) {
        adminButtons = (
          <>
            {signalId && (
              <CancelSignalTrigger
                trigger={<Button>{t("drawer.close")}</Button>}
                signalId={signalId}
              />
            )}
            <Button onClick={() => onEditModeClicked()}>
              {t("drawer.edit")}
            </Button>
          </>
        );
      }
    }
    const followActions = signalFollowData ? (
      <SignalFollowActions signal={signalFollowData} isDrawer={true} />
    ) : undefined;

    return (
      <>
        {adminButtons}
        {!forAdmin && !isEdit && followActions}
      </>
    );
  };

  return (
    <DrawerTitleLayout
      leftActions={renderLeftActions()}
      rightActions={
        isEdit && (
          <DebouncedButton
            onClick={onSaveChangesClick}
            loading={editSignalLoading || editSignalConfigurationLoading}
          >
            {t("drawer.save")}
          </DebouncedButton>
        )
      }
      titleRowActions={
        signalFollowData && showEllipsisSignalActions ? (
          <SignalActions
            signalFollowData={signalFollowData}
            onEditModeClicked={onEditModeClicked}
          />
        ) : undefined
      }
      title={
        isNewLook ? (
          <NewLookDrawerTitle
            symbol={symbol}
            side={signalGeneralData?.general?.["Long/Short"]}
            leverage={parseLeverage(signalGeneralData?.general?.["Leverage"])}
            status={isOpen ? t("status.open") : t("status.closed")}
            compact={isPhone}
          />
        ) : (
          <TextWithIcon
            text={getTitleText()}
            coinOrExchangeName={symbol}
            iconType={ICON_TYPES.COIN}
            iconSize={"m"}
            justify={"center"}
            style={{
              visibility: !symbol ? "hidden" : "visible",
              width: "100%",
            }}
          />
        )
      }
    />
  );
};

export default DrawerTitle;
